<script setup lang="ts">
import WrIcon from "~/components/global/WrIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface SubItem {
  label: string;
}

interface FlowStage {
  id: string | number;
  icon: string;
  title: string;
  subtitle: string;
  items: SubItem[];
}

interface Capability {
  icon: string;
  label: string;
}

const props = withDefaults(
  defineProps<{
    title?: string;
    stages: FlowStage[];
    capabilities?: Capability[];
  }>(),
  {
    title: "Lifecycle Flow",
    capabilities: () => [],
  },
);
</script>

<template>
  <section
    class="relative w-full overflow-hidden mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]"
    aria-label="Lifecycle flow diagram"
  >
    <!-- Background: grid pattern (dark only) -->
    <div
      v-if="!isLight"
      class="absolute inset-0 pointer-events-none"
      style="
        background:
          linear-gradient(rgba(139, 92, 246, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(139, 92, 246, 0.03) 1px, transparent 1px);
        background-size: 60px 60px;
      "
    ></div>

    <div class="relative z-10 max-w-screen-xl mx-auto px-4">
      <!-- Title -->
      <div class="text-center mb-10 lg:mb-14">
        <h2
          class="text-[28px] lg:text-[48px] font-semibold leading-tight font-avant-demi"
          :class="isLight ? 'text-[#613FAA]' : 'text-white'"
        >
          {{ title }}
        </h2>
        <span
          class="block mx-auto mt-3 h-[2px] rounded-full w-[100px]"
          style="background: linear-gradient(92deg, #613FAA 0%, #BA55FE 100%)"
        ></span>
      </div>

      <!-- Stages Grid -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
        <template v-for="(stage, index) in stages" :key="stage.id">
          <div class="flex flex-col items-center relative h-full">
            <!-- Stage Card -->
            <div
              class="stage-card relative w-full h-full rounded-2xl p-6 flex flex-col items-center justify-center gap-3 text-center"
              :class="isLight ? 'light-card' : 'dark-card'"
            >
              <!-- Badge (top-left corner) -->
              <span
                class="absolute top-4 left-4 flex items-center justify-center px-2.5 py-1 rounded-full text-[11px] font-semibold text-white"
                style="background: #7C3AED"
              >
                {{ stage.id }}
              </span>

              <!-- Large centered icon -->
              <WrIcon
                v-if="stage.icon"
                :name="stage.icon"
                :size="44"
                class="mx-auto mt-2 block"
                :class="isLight ? 'text-[#7C3AED]' : 'text-[#A78BFA]'"
              />

              <!-- Stage Title -->
              <h3
                class="text-[20px] font-semibold leading-tight font-avant-demi"
                :class="isLight ? 'text-[#613FAA]' : 'text-white'"
              >
                {{ stage.title }}
              </h3>

              <!-- Stage Subtitle -->
              <p
                class="text-[13px] font-avant leading-relaxed"
                :class="isLight ? 'text-gray-600' : 'text-[rgba(255,255,255,0.55)]'"
              >
                {{ stage.subtitle }}
              </p>
            </div>

            <!-- Wavy arrow to next stage (desktop only) -->
            <svg
              v-if="index < stages.length - 1"
              class="hidden lg:block absolute top-[48px] -right-[24px] w-[24px] h-[24px] z-20 pointer-events-none"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M 2 12 C 6 12, 6 4, 12 4 C 18 4, 18 20, 22 12"
                stroke="url(#wavyGrad${stage.id})"
                stroke-width="2"
                stroke-linecap="round"
                fill="none"
              />
              <path
                d="M 17 7 L 22 12 L 17 17"
                stroke="url(#wavyGrad${stage.id})"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
              <defs>
                <linearGradient :id="'wavyGrad' + stage.id" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stop-color="#8B5CF6" />
                  <stop offset="100%" stop-color="#BA55FE" />
                </linearGradient>
              </defs>
            </svg>

            <!-- Vertical connector line from card to sub-items -->
            <div
              class="w-[2px] h-5 rounded-full"
              :style="{ background: isLight ? 'rgba(97,63,170,0.3)' : 'rgba(139,92,246,0.4)' }"
            ></div>

            <!-- Sub-items (pills) -->
            <div class="flex flex-col gap-2.5 w-full">
              <span
                v-for="(item, i) in stage.items"
                :key="i"
                class="sub-pill px-4 py-2 rounded-full text-[13px] font-avant font-medium text-center"
                :class="isLight ? 'light-pill' : 'dark-pill'"
              >
                {{ item.label }}
              </span>
            </div>
          </div>
        </template>
      </div>

      <!-- Loop-back arc (desktop) - positioned above the entire grid -->
      <svg
        class="hidden lg:block absolute left-0 w-full pointer-events-none"
        style="top: -80px; height: 80px;"
        viewBox="0 0 1200 80"
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M 1060 60 C 1060 0, 140 0, 140 60"
          stroke="url(#arcGrad)"
          stroke-width="2.5"
          stroke-linecap="round"
          fill="none"
        />
        <!-- Arrowhead at left end -->
        <path
          d="M 132 52 L 140 60 L 148 52"
          stroke="url(#arcGrad)"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          fill="none"
        />
        <defs>
          <linearGradient id="arcGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stop-color="#BA55FE" />
            <stop offset="100%" stop-color="#8B5CF6" />
          </linearGradient>
        </defs>
      </svg>

      <!-- Mobile loop-back indicator -->
      <!-- <div class="flex lg:hidden justify-center mt-8">
        <div
          class="flex items-center gap-2 text-sm font-avant"
          :class="isLight ? 'text-[#613FAA]' : 'text-[#BA55FE]'"
        >
          <WrIcon name="streamline:arrow-infinite-loop" :size="18" />
          <span>Continuous cycle</span>
        </div>
      </div> -->


    </div>
  </section>
</template>

<style scoped>
/* ── Stage Card ── */
.stage-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stage-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #12dbff, #ba55fe);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stage-card:hover::before {
  opacity: 1;
}

.stage-card:hover {
  transform: translateY(-3px);
}

/* Light mode cards */
.light-card {
  background: linear-gradient(135deg, #ffffff 0%, #faf5ff 100%);
  border: 1px solid #e7cfff;
}

.light-card:hover {
  box-shadow: 0 8px 24px rgba(166, 85, 246, 0.15);
  border-color: rgba(186, 85, 254, 0.35);
}

/* Dark mode cards */
.dark-card {
  background: linear-gradient(135deg, rgba(1, 3, 20, 0.6) 0%, rgba(43, 1, 82, 0.4) 100%);
  border: 1px solid rgba(139, 92, 246, 0.25);
}

.dark-card:hover {
  box-shadow: 0 8px 32px rgba(166, 85, 246, 0.2), 0 0 0 1px rgba(186, 85, 254, 0.15);
  border-color: rgba(186, 85, 254, 0.4);
}

/* ── Sub-item Pills ── */
.sub-pill {
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, background 0.25s ease;
}

.sub-pill:hover {
  transform: translateX(4px);
}

.light-pill {
  background: rgba(97, 63, 170, 0.06);
  border: 1px solid rgba(97, 63, 170, 0.15);
  color: #613faa;
}

.light-pill:hover {
  background: rgba(97, 63, 170, 0.12);
  border-color: rgba(186, 85, 254, 0.3);
  box-shadow: 0 2px 8px rgba(166, 85, 246, 0.1);
}

.dark-pill {
  background: linear-gradient(135deg, rgba(1, 3, 20, 0.5) 0%, rgba(43, 1, 82, 0.35) 100%);
  border: 1px solid rgba(139, 92, 246, 0.2);
  color: rgba(255, 255, 255, 0.8);
}

.dark-pill:hover {
  background: linear-gradient(135deg, rgba(1, 3, 20, 0.7) 0%, rgba(43, 1, 82, 0.55) 100%);
  border-color: rgba(186, 85, 254, 0.4);
  box-shadow: 0 4px 16px rgba(166, 85, 246, 0.15);
}

/* ── Capabilities Bar ── */
.caps-bar {
  border-radius: 9999px;
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
}

.caps-bar:hover {
  box-shadow: 0 4px 20px rgba(166, 85, 246, 0.1);
}

.light-caps {
  background: linear-gradient(135deg, #ffffff 0%, #faf5ff 100%);
  border: 1px solid #e7cfff;
}

.dark-caps {
  background: linear-gradient(135deg, rgba(1, 3, 20, 0.5) 0%, rgba(43, 1, 82, 0.35) 100%);
  border: 1px solid rgba(139, 92, 246, 0.2);
}

.caps-divider {
  background: currentColor;
}
</style>
